import { TrashIcon } from '@heroicons/react/20/solid';
import DatePicker from 'antd/es/date-picker';
import dayjs from 'dayjs';
import { Plus } from 'lucide-react';
import { FC, useEffect, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useContextSelector } from 'use-context-selector';

import { getUserProfileParticipants } from '@/core/apis/orders-apis';
import { authSelector } from '@/core/store/global/app-states';
import { AppContext } from '@/core/store/global/AppProvider';
import { Button } from '@/ux/components/shadcn/ui/button';
import { Input } from '@/ux/components/shadcn/ui/input';

type Participant = {
  _id: string;
  name: string;
  email: string;
  monthAndYearOfBirth: any;
  gender: string;
  allergies: string;
};

type Product = {
  assetId: string;
  name: string;
  participants: Participant[];
};

type FormData = {
  participantInfo: Product[];
};

type ParticipantInfoProps = {
  onNext: any;
  onBack: any;
  setParticipantData: any;
  participantData: any;
  stage: number;
  participantCountData: any[];
  consentForPhotos: boolean;
  setConsentForPhotos: any;
};
const ParticipantInfo: FC<ParticipantInfoProps> = ({
  onNext,
  setParticipantData,
  participantData,
  consentForPhotos,
  setConsentForPhotos,
  stage,
  onBack,
  participantCountData,
}) => {
  const formattedParticipantData = participantData?.map((product: any) => ({
    assetId: product.assetId,
    name: product?.name,
    count: product?.count,
    participants: product?.participants?.map((participant: any) => ({
      ...participant,
      monthAndYearOfBirth:
        participant?.monthAndYearOfBirth && dayjs(participant?.monthAndYearOfBirth, 'YYYY-MM', true).isValid()
          ? dayjs(participant?.monthAndYearOfBirth, 'YYYY-MM')
          : null,
    })),
  }));

  const [selectedProductIndex, setSelectedProductIndex] = useState(0);
  const authentication = useContextSelector(AppContext, authSelector);
  const {
    register,
    control,
    watch,
    getValues,
    setValue,
    trigger,
    formState: { isValid, errors },
  } = useForm<FormData>({
    mode: 'all',
    defaultValues: {
      participantInfo: participantData
        ? formattedParticipantData
        : participantCountData.map((product) => ({
            assetId: product.assetId,
            name: product?.name,
            count: product?.count,
            participants: [
              {
                _id: '',
                name: '',
                email: '',
                monthAndYearOfBirth: null as any,
                gender: '',
                allergies: '',
              },
            ],
          })),
    },
  });

  const { fields, update } = useFieldArray({
    control,
    name: 'participantInfo',
  });

  useEffect(() => {
    if (authentication?.loggedIn) {
      fetchSavedAddresses();
    }
  }, [authentication]);

  const fetchSavedAddresses = async () => {
    getUserProfileParticipants().then((res: any) => {
      setSavedParticipants(res);
    });
  };

  const [savedParticipants, setSavedParticipants] = useState<any>([]);
  const [selectedAsset, setSelectedAsset] = useState<any>(participantCountData?.[0] || null);

  const handleAddParticipant = (productIndex: number) => {
    const updatedProduct: any = {
      ...participantInfo[productIndex],
      participants: [
        ...(participantInfo[productIndex].participants || []),
        { _id: '', name: '', email: '', monthAndYearOfBirth: null as any, gender: '', allergies: '' },
      ],
    };
    update(productIndex, updatedProduct);
  };

  const handleRemoveParticipant = (productIndex: number, participantIndex: number) => {
    const updatedParticipants = participantInfo[productIndex].participants.filter(
      (_: any, index: number) => index !== participantIndex,
    );
    const updatedProduct = {
      ...fields[productIndex],
      participants: updatedParticipants,
    };
    update(productIndex, updatedProduct);
  };

  const handleSubmit = (e: any) => {
    if (!isValid) return;
    const data = getValues('participantInfo');
    const formattedData = data?.map((product: any) => ({
      assetId: product.assetId,
      name: product?.name,
      participants: product?.participants?.map((participant: any) => ({
        ...participant,
        monthAndYearOfBirth: participant.monthAndYearOfBirth
          ? participant.monthAndYearOfBirth.format('YYYY-MM')
          : '',
      })),
    }));
    setParticipantData(formattedData);
    onNext();
  };

  const onSelectParticipant: any = (e: any, productIndex: number, participantIndex: number) => {
    const participant: any = savedParticipants?.find(
      (participant: any) => participant?.guid == e.target.value,
    );
    if (participant) {
      const { name, email, gender, monthAndYearOfBirth, allergies } = participant;
      setValue(`participantInfo.${productIndex}.participants.${participantIndex}`, {
        _id: participant?.guid,
        name,
        email,
        gender,
        monthAndYearOfBirth:
          monthAndYearOfBirth && dayjs(monthAndYearOfBirth, 'YYYY-MM', true).isValid()
            ? dayjs(monthAndYearOfBirth, 'YYYY-MM')
            : null,
        allergies,
      });
      trigger();
    } else {
      setValue(`participantInfo.${productIndex}.participants.${participantIndex}`, {
        _id: '',
        name: '',
        email: '',
        gender: '',
        monthAndYearOfBirth: null,
        allergies: '',
      });
    }
  };

  const handleDateChange = (date: dayjs.Dayjs | null, index: number, productIndex: number) => {
    setValue(`participantInfo.${productIndex}.participants.${index}.monthAndYearOfBirth`, date);
  };

  const participantInfo = watch('participantInfo');

  const onSelectAssetItem = (assetInfo: any, index: number) => {
    setSelectedAsset(assetInfo);
    setSelectedProductIndex(index);
  };

  return (
    <div className="flex gap-2 flex-col h-full">
      {participantCountData?.length > 1 && (
        <div className="flex gap-2 justify-center py-2">
          {participantCountData?.map((item: any, index: number) => (
            <button
              key={index}
              className={`${
                item?.assetId == selectedAsset?.assetId ? 'bg-black text-white' : 'bg-gray-200'
              } p-2 text-sm px-4`}
              onClick={() => onSelectAssetItem(item, index)}
            >
              {item?.name}
            </button>
          ))}
        </div>
      )}
      <div className="grow overflow-y-auto">
        <div className="space-y-4 p-1">
          {fields?.map((product: any, productIndex) => {
            return (
              selectedProductIndex == productIndex && (
                <div key={productIndex} className="space-y-8 p-2 rounded-md">
                  {product.participants.map((participant: any, participantIndex: number) => (
                    <div className="mt-4 space-y-2" key={participantIndex}>
                      {(participantIndex > 0 || product?.participants?.length > 1) && (
                        <div className="p-2 w-full flex flex-row justify-end">
                          <button
                            type="button"
                            onClick={() => handleRemoveParticipant(productIndex, participantIndex)}
                          >
                            <TrashIcon className="h-4 w-4 text-red-500" />
                          </button>
                        </div>
                      )}

                      {authentication?.loggedIn && (
                        <select
                          className="w-full border-gray-200 rounded-md focus:border-gray-200 focus:ring-gray-200"
                          onChange={(e: any) => onSelectParticipant(e, productIndex, participantIndex)}
                        >
                          <option>Select saved or enter new participant...</option>
                          {savedParticipants?.map((participant: any) => {
                            return (
                              <option value={participant?.guid} key={participant?.guid}>
                                {participant?.name}
                              </option>
                            );
                          })}
                        </select>
                      )}

                      <Input
                        placeholder="Name"
                        maxLength={30}
                        {...register(`participantInfo.${productIndex}.participants.${participantIndex}.name`)}
                      />
                      <Input
                        placeholder="Email"
                        type="email"
                        {...register(
                          `participantInfo.${productIndex}.participants.${participantIndex}.email`,
                          {
                            pattern: {
                              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                              message: 'invalid email address',
                            },
                          },
                        )}
                      />
                      <div>
                        {errors?.participantInfo?.[productIndex]?.participants?.[participantIndex]?.email && (
                          <span className="text-red-500 text-sm">{`Invlaid Email`}</span>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <select
                          className="w-full border-gray-200 rounded-md focus:border-gray-200 focus:ring-gray-200"
                          {...register(
                            `participantInfo.${productIndex}.participants.${participantIndex}.gender`,
                          )}
                        >
                          <option value="" disabled selected>
                            Gender
                          </option>
                          <option value="male" key="male">
                            Male
                          </option>
                          <option value="female" key="female">
                            Female
                          </option>
                          <option value="other" key="other">
                            Other
                          </option>
                        </select>
                        <DatePicker
                          inputReadOnly
                          picker="month"
                          value={
                            participantInfo[productIndex]?.participants[participantIndex]?.monthAndYearOfBirth
                              ? dayjs(
                                  participantInfo[productIndex]?.participants[participantIndex]
                                    .monthAndYearOfBirth,
                                  'YYYY-MM',
                                )
                              : null
                          }
                          onChange={(e: any) => handleDateChange(e, participantIndex, productIndex)}
                          format="YYYY-MM"
                          getPopupContainer={(triggerNode) => {
                            return document.getElementById('checkoutPopup') as HTMLElement;
                          }}
                          className="w-full p-2 border-gray-200 rounded-md bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-black sm:text-sm"
                          disabledDate={(current) => current.isAfter(dayjs(), 'month')}
                        />
                      </div>
                      <Input
                        placeholder="Allergies"
                        {...register(
                          `participantInfo.${productIndex}.participants.${participantIndex}.allergies`,
                        )}
                      />
                      <hr className="my-2" />
                    </div>
                  ))}

                  {product.participants?.length < product.count && (
                    <Button
                      onClick={() => handleAddParticipant(productIndex)}
                      variant="outline"
                      className="w-full"
                    >
                      <Plus className="mr-2 h-4 w-4" /> Add Another Participant
                    </Button>
                  )}
                </div>
              )
            );
          })}
        </div>
        <div className="p-4">
          <input
            type="checkbox"
            style={{ borderColor: 'black' }}
            defaultChecked={consentForPhotos}
            onChange={(e) => setConsentForPhotos(e.target.checked)}
          />{' '}
          Consent for Photos
        </div>
      </div>
      <div className={`flex ${stage > 0 ? 'justify-between' : 'justify-end'} p-2`}>
        {stage > 0 && (
          <Button onClick={onBack} className="bg-black text-white">
            Back
          </Button>
        )}
        <Button type="submit" className="bg-black text-white" onClick={handleSubmit}>
          Next
        </Button>
      </div>
    </div>
  );
};

export default ParticipantInfo;
